/**
 * 移动端底部 TabBar：时间相册 / 那些话 / [管理] / 我（PRD §4.4）。
 *
 * 「管理」tab 仅 owner 可见（普通用户身份不渲染 DOM 节点）。
 * 图标是钥匙（ManageIcon），不是齿轮，避免与"设置"语义混淆。
 *
 * 顶部 header 已有搜索入口，首页（/home）作为单独路由不在底部 tab 内，
 * 停留在首页时全部 tab 处于未激活态。
 */

import React from 'react';
import { AlbumIcon, LetterIcon, UserIcon, ManageIcon } from './icons';
import { useRole } from '@/shared/auth';
import { useUnreadBadge } from './unreadBadge';

export type TabKey = 'album' | 'letters' | 'manage' | 'account';

export interface MobileTabBarProps {
  /** 当前激活 tab；null 表示停留在非 tab 页（如 /home） */
  active: TabKey | null;
  onChange: (next: TabKey) => void;
}

interface TabSpec {
  key: TabKey;
  label: string;
  Icon: React.FC<{ size?: number }>;
  testId: string;
  ownerOnly?: boolean;
}

const TAB_DEFS: TabSpec[] = [
  { key: 'album', label: '相册', Icon: AlbumIcon, testId: 'tab-album' },
  { key: 'letters', label: '那些话', Icon: LetterIcon, testId: 'tab-letters' },
  { key: 'manage', label: '管理', Icon: ManageIcon, testId: 'tab-manage', ownerOnly: true },
  { key: 'account', label: '我', Icon: UserIcon, testId: 'tab-account' },
];

export const MobileTabBar: React.FC<MobileTabBarProps> = ({ active, onChange }) => {
  const { isOwner } = useRole();
  const { count: unreadCount } = useUnreadBadge();
  const visibleTabs = TAB_DEFS.filter((t) => !t.ownerOnly || isOwner);

  return (
    <nav
      className={`fp-mobile-tabbar fp-mobile-tabbar--count-${visibleTabs.length}`}
      aria-label="底部导航"
    >
      {visibleTabs.map((tab) => {
        const isActive = active === tab.key;
        const { Icon } = tab;
        return (
          <button
            key={tab.key}
            type="button"
            className={
              'fp-mobile-tabbar__item' + (isActive ? ' fp-mobile-tabbar__item--active' : '')
            }
            data-testid={tab.testId}
            onClick={() => onChange(tab.key)}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon size={22} />
            {tab.key === 'account' && unreadCount > 0 ? (
              <span className="fp-mobile-tabbar__unread-dot" data-testid="tab-account-unread-dot" aria-label={`${unreadCount} 条未读通知`} />
            ) : null}
            <span>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default MobileTabBar;
